import { useState } from 'react';

const STORAGE_PREFIX = 'pp_column_settings:';
const EMPTY_SETTINGS = { order: [], hidden: [] };

// PPResizableTable-ийн түгжээтэй баганын дүрэмтэй ижил: эхний багана
// (дугаарлалт) болон `fixed` багана тохиргоонд оролцохгүй, үргэлж харагдана
const isLockedColumn = (col, index, lockFirstColumn) =>
  (lockFirstColumn && index === 0) || Boolean(col.fixed);

// getColumnSearchProps гарчгийг SearchHeader элементээр сольдог тул
// нэрийг нь label prop-оос авна
const getColumnLabel = (col) =>
  typeof col.title === 'string' ? col.title : (col.title?.props?.label ?? col.key);

const readSettings = (storageKey) => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(STORAGE_PREFIX + storageKey));
    return {
      order: Array.isArray(saved?.order) ? saved.order : [],
      hidden: Array.isArray(saved?.hidden) ? saved.hidden : [],
    };
  } catch {
    return EMPTY_SETTINGS;
  }
};

const writeSettings = (storageKey, settings) => {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + storageKey, JSON.stringify(settings));
  } catch {
    // localStorage хаалттай (private горим г.м.) бол зөвхөн state-д үлдэнэ
  }
};

/**
 * Хүснэгтийн баганыг нуух/харуулах, байр солих тохиргоо. Тохиргоо нь
 * localStorage-д `storageKey`-ээр хадгалагдаж refresh хийсэн ч үлдэнэ.
 *
 *   const { columns, settingsProps, onColumnOrderChange } =
 *     useColumnSettings({ columns: allColumns, storageKey: 'orderDelivery' });
 *
 *   <ColumnSettings {...settingsProps} />
 *   <PPResizableTable columns={columns} onColumnOrderChange={onColumnOrderChange} />
 */
export const useColumnSettings = ({ columns, storageKey, lockFirstColumn = true }) => {
  const [settings, setSettings] = useState(() => readSettings(storageKey));

  const updateSettings = (next) => {
    setSettings(next);
    writeSettings(storageKey, next);
  };

  // Хадгалсан эрэмбэд байхгүй (шинээр нэмэгдсэн) баганууд төгсгөлд нь орно
  const orderIndex = new Map(settings.order.map((key, i) => [key, i]));
  const configurableColumns = columns.filter(
    (col, index) => !isLockedColumn(col, index, lockFirstColumn)
  );
  const sortedColumns = configurableColumns
    .map((col, index) => ({ col, rank: orderIndex.get(col.key) ?? settings.order.length + index }))
    .sort((a, b) => a.rank - b.rank)
    .map(({ col }) => col);
  const sortedKeys = sortedColumns.map((col) => col.key);

  const hidden = new Set(settings.hidden);
  let sortedIndex = 0;
  const visibleColumns = columns
    .map((col, index) =>
      isLockedColumn(col, index, lockFirstColumn) ? col : sortedColumns[sortedIndex++]
    )
    .filter((col, index) => isLockedColumn(col, index, lockFirstColumn) || !hidden.has(col.key));

  const toggleColumn = (key, visible) => {
    const nextHidden = visible
      ? settings.hidden.filter((hiddenKey) => hiddenKey !== key)
      : [...settings.hidden, key];
    updateSettings({ order: sortedKeys, hidden: nextHidden });
  };

  // Тохиргооны жагсаалт дотор drag хийж байр солих (нуусан баганууд ч орно)
  const moveColumn = (fromIndex, toIndex) => {
    const nextOrder = [...sortedKeys];
    const [moved] = nextOrder.splice(fromIndex, 1);
    nextOrder.splice(toIndex, 0, moved);
    updateSettings({ order: nextOrder, hidden: settings.hidden });
  };

  // Хүснэгтийн толгой дээр drag хийхэд зөвхөн харагдаж буй баганууд ирдэг тул
  // нуусан баганууд байрандаа үлдэж, харагдах баганууд л шинэ эрэмбээр сууна
  const onColumnOrderChange = (visibleKeys) => {
    const visibleQueue = [...visibleKeys];
    const nextOrder = sortedKeys.map((key) => (hidden.has(key) ? key : visibleQueue.shift()));
    updateSettings({ order: nextOrder, hidden: settings.hidden });
  };

  const resetSettings = () => updateSettings(EMPTY_SETTINGS);

  return {
    columns: visibleColumns,
    onColumnOrderChange,
    settingsProps: {
      items: sortedColumns.map((col) => ({
        key: col.key,
        label: getColumnLabel(col),
        visible: !hidden.has(col.key),
      })),
      // Нуусан багана байхгүй, эрэмбэ анхныхтай ижил бол "Сэргээх" идэвхгүй
      isCustomized:
        visibleColumns.length !== columns.length ||
        sortedColumns.some((col, index) => col !== configurableColumns[index]),
      onToggle: toggleColumn,
      onMove: moveColumn,
      onReset: resetSettings,
    },
  };
};
